// Third party
import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PerfectScrollbar from 'react-perfect-scrollbar';
import get from 'lodash/get';
import uniq from 'lodash/uniq';

// Components
import { DateQuickRangeOneWay } from '../inputs';
import PerformanceDisclaimer from '../disclaimers/comparison/PerformanceDisclaimer';
import RiskDisclaimer from '../disclaimers/comparison/RiskDisclaimer';
import { GrowthCell, PercentageCell, RiskRatingCell, TextCell, StatCell, UpDownCell } from '../cells';
import { PrimaryButton, SecondaryButton, SecondaryLink } from '../buttons';
import ConnectedPrivateComponent from '../PrivateComponent';
import ShelfStatusIcon from '../shelf-management/ShelfStatusIcon';
import WarningLabel from '../misc/WarningLabel';
import ShelfStatus from '../shelf-management/ShelfStatus';
import InstrumentComparisonTable from '../compliance-comparison/InstrumentComparisonTable';
import { Breadcrumb, BreadcrumbItem } from '../navigation';
import FundDetailsESGMetrics from './FundDetailsESGMetrics';
import GrowthChart from '../charts/deprecated/charts/GrowthChart';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage } from '../../../utils/i18n';
import { fundToComparisonItem } from '../../../utils/comparison';
import { NAV_PATHS, TIME_PERIODS, TIME_RANGE_PRESET } from '../../../utils/constants';
import { COMPLIANT_INSTRUMENT, ShelfStatus as ShelfStatusEnum, statusesConfig } from '../../../utils/shelf-management';
import { PERMISSIONS } from '../../../utils/permissions';
import { showError } from '../../../utils/errors';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { addFundsToComparison } from '../../../redux/modules/comparison';
import { setSelectedFunds, updateFundsResult } from '../../../redux/modules/advanced-search';
import { addFundComplianceAlternatives, ADD_COMPLIANCE_ALTERNATIVES, clearComplianceAlternatives } from '../../../redux/modules/compliance-alternatives';
import { selectIsLoadingEvery } from '../../../redux/modules/ui/loaders';
import User from '@/types/user';
import Fund from '@/types/fund';
import Locale from '@/types/locale';
import RiskPeriods from '@/types/risk-periods';
import AppRegion from '@/types/app-region';
import { getDateQuickRangeFromPreset } from '@/utils/time-range-presets';
import { HOLDING_TYPES } from '@/utils/portfolios';

type StateProps = {
  user?: User | null;
  advancedSearchFunds?: Fund[];
  complianceAlternatives: Fund[];
  locale: Locale;
  selectedFunds: Fund[];
  comparingAlternatives: boolean;
};

type DispatchProps = {
  addFundComplianceAlternatives: (id: string) => Promise<string>;
  addFundsToComparison: (funds: Fund[]) => Promise<void>;
  clearComplianceAlternatives: () => void;
  setSelectedFunds: (funds: Fund[]) => void;
  updateFundsResult: (funds: Fund[]) => void;
};

type Props = StateProps &
  DispatchProps &
  RouteComponentProps & {
    fund: Fund;
    className?: string;
    onClose?: () => void;
    showAddToPortfolio?: boolean;
    onAddToPortfolio?: (fund: Fund) => void;
    onCompareAlternatives: (id: string) => void;
  };

type State = {
  riskPeriod: RiskPeriods;
};

type Field = {
  i18nKey: string;
  cell?: React.FunctionComponent;
  key: string;
  accessor?: (fund: Fund, riskPeriod: string) => any;
  multiValue?: boolean;
};

type FieldGroup = {
  i18nKey: string;
  fields: Field[];
  controlled?: boolean;
  getDisclaimer?: (fund: Fund) => JSX.Element;
};

class FundDetails extends React.Component<Props, State> {
  readonly state: State = {
    riskPeriod: TIME_PERIODS['3Y'],
  };

  onRiskChange = (dateQuickRange: TIME_PERIODS) => {
    this.setState({ riskPeriod: dateQuickRange as RiskPeriods });
  };

  compareAlternatives = async () => {
    try {
      const { addFundComplianceAlternatives, advancedSearchFunds, fund, onCompareAlternatives, updateFundsResult } = this.props;

      const complianceComparisonId = await addFundComplianceAlternatives(fund._id);
      if (advancedSearchFunds) {
        const updatedFunds = advancedSearchFunds.map(searchFund => {
          if (searchFund._id === fund._id) {
            return {
              ...searchFund,
              complianceComparisons: [...(searchFund.complianceComparisons || []), complianceComparisonId],
            };
          }

          return searchFund;
        });

        updateFundsResult(updatedFunds);
      }
      onCompareAlternatives(fund._id);
    } catch (err) {
      showError(err);
    }
  };

  compareAll = () => {
    const { addFundsToComparison, complianceAlternatives, history } = this.props;
    addFundsToComparison(complianceAlternatives).then(() => {
      history.push(NAV_PATHS.COMPARISON, { from: this.props.location });
    });
  };

  selectAlternativeFund = async (fund: Fund) => {
    const { selectedFunds, setSelectedFunds } = this.props;

    setSelectedFunds(uniq([...selectedFunds, fund]));
  };

  generateBreadcrumbs = (hasComplianceAlternatives: boolean): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [
      {
        value: formatMessage('FundDetails.Title.Root'),
        onClick: this.props.clearComplianceAlternatives,
      },
    ];
    if (hasComplianceAlternatives) {
      return [
        ...breadcrumbs,
        {
          value: formatMessage('FundDetails.Title.Alternatives'),
        },
      ];
    }

    return breadcrumbs;
  };

  generateField = (fund: Fund, field: Field) => {
    const value = field.accessor && field.multiValue ? field.accessor(fund, this.state.riskPeriod) : get(fund, field.key);
    return React.createElement((field.cell as any) || TextCell, {
      value,
    });
  };

  getFieldGroups = (riskPeriod: string) => {
    const { locale, user } = this.props;

    const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);

    const FIELD_GROUPS: FieldGroup[] = [
      {
        i18nKey: 'FundDetails.Stats.Performance',
        getDisclaimer: (fund: Fund) => <PerformanceDisclaimer showItemNames={false} items={[fundToComparisonItem(fund, appRegion)]} />,
        fields: [
          { i18nKey: 'FundDetails.Stats.YTD', cell: GrowthCell, key: 'performance.percentGrowth["YT"]' },
          { i18nKey: 'FundDetails.Stats.1Y', cell: GrowthCell, key: 'performance.percentGrowth["1Y"]' },
          { i18nKey: 'FundDetails.Stats.3Y', cell: GrowthCell, key: 'performance.percentGrowth["3Y"]' },
          { i18nKey: 'FundDetails.Stats.5Y', cell: GrowthCell, key: 'performance.percentGrowth["5Y"]' },
          { i18nKey: 'FundDetails.Stats.INC', cell: GrowthCell, key: 'performance.percentGrowth["INC"]' },
        ],
      },
      {
        i18nKey: 'FundDetails.Stats.KeyMetrics',
        fields: [
          { i18nKey: 'FundDetails.Stats.MER', cell: PercentageCell, key: 'mer' },
          { i18nKey: 'RiskMetrics.RiskRating', cell: RiskRatingCell, key: `riskRating[${locale}]` },
        ],
      },
      {
        i18nKey: 'FundDetails.Stats.RiskMetrics',
        controlled: true,
        getDisclaimer: (fund: Fund) => <RiskDisclaimer showItemNames={false} items={[fundToComparisonItem(fund, appRegion)]} />,
        fields: [
          { i18nKey: 'RiskMetrics.Beta', key: `performance.beta.${riskPeriod}`, cell: StatCell, multiValue: true },
          { i18nKey: 'FundDetails.Stats.StandardDeviation', key: `performance.standardDeviation.${riskPeriod}`, cell: StatCell, multiValue: true },
          { i18nKey: 'FundDetails.Stats.MaximumDrawdown', key: `performance.maximumDrawdown.${riskPeriod}`, cell: StatCell, multiValue: true },
          {
            i18nKey: 'FundDetails.Stats.UpsideDownside',
            cell: UpDownCell,
            accessor: (fund: Fund, period: string) => ({
              upside: fund.performance.upside[period as RiskPeriods],
              downside: fund.performance.downside[period as RiskPeriods],
            }),
            key: 'performance.upsideDownside',
            multiValue: true,
          },
        ],
      },
    ];
    return FIELD_GROUPS;
  };

  render() {
    const { user, fund, className, onClose, showAddToPortfolio, complianceAlternatives, clearComplianceAlternatives, comparingAlternatives, locale } =
      this.props;
    const { riskPeriod } = this.state;

    const name = fund.names ? formatLocalizedString(fund.names.full) : null;
    const symbols = fund.symbols && fund.symbols.sort().join(', ');
    const showKYPComplianceButton =
      fund.flags && (fund.flags.includes(statusesConfig.shelf_onShelf.key) || fund.flags.includes(statusesConfig.shelf_onShelfKYPComplete.key));
    const showKYPComplianceWarning = fund.flags && fund.flags.includes(statusesConfig.shelf_onShelf.key);
    const hasComplianceAlternatives = complianceAlternatives.length > 0;
    const isCompliant = fund.complianceComparisons?.includes(COMPLIANT_INSTRUMENT);

    if (!user) {
      return null;
    }

    const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);

    return (
      <div className={classNames('fund-details item-details', className)}>
        <div className="details-container d-flex flex-column">
          <div className="d-flex details-header">
            <Breadcrumb items={this.generateBreadcrumbs(hasComplianceAlternatives)} />
            <div className="fund-details-actions">
              {onClose && (
                <SecondaryLink onClick={() => onClose()} icon="arrow-down-left2">
                  <FormattedMessage id="FundDetails.Hide" />
                </SecondaryLink>
              )}
            </div>
          </div>

          <PerfectScrollbar>
            <div className="fund-header">
              <div>
                <h6 className="fund-symbols">{symbols}</h6>
                <h4 className="fund-name">
                  {name}
                  {fund.flags && <ShelfStatus statuses={fund.flags} />}
                </h4>
                {showKYPComplianceWarning && (
                  <ConnectedPrivateComponent permissions={[PERMISSIONS.KYP]}>
                    {() => <WarningLabel text={formatMessage(`FundDetails.Warning.${statusesConfig[ShelfStatusEnum.ON_SHELF].key}`)} />}
                  </ConnectedPrivateComponent>
                )}
              </div>

              {showKYPComplianceButton && !hasComplianceAlternatives && !isCompliant && (
                <ConnectedPrivateComponent permissions={[PERMISSIONS.KYP]}>
                  {() => (
                    <PrimaryButton loading={comparingAlternatives} className="compare-alternatives" onClick={this.compareAlternatives}>
                      {comparingAlternatives ? (
                        <FormattedMessage id="FundDetails.LoadingAlternatives" />
                      ) : (
                        <>
                          <ShelfStatusIcon />
                          <FormattedMessage id="FundDetails.CompareAlternatives" />
                        </>
                      )}
                    </PrimaryButton>
                  )}
                </ConnectedPrivateComponent>
              )}

              {hasComplianceAlternatives && (
                <ConnectedPrivateComponent permissions={[PERMISSIONS.KYP]}>
                  {() => (
                    <SecondaryButton compact onClick={this.compareAll}>
                      <FormattedMessage id="FundDetails.CompareAll" />
                    </SecondaryButton>
                  )}
                </ConnectedPrivateComponent>
              )}

              {showAddToPortfolio && (
                <PrimaryButton compact onClick={() => this.props.onAddToPortfolio && this.props.onAddToPortfolio(fund)}>
                  <FormattedMessage id="Search.Funds.AddToPortfolio" />
                </PrimaryButton>
              )}
            </div>

            {hasComplianceAlternatives ? (
              <InstrumentComparisonTable<Fund>
                complianceAlternatives={complianceAlternatives}
                clearComplianceAlternatives={clearComplianceAlternatives}
                instrumentType={HOLDING_TYPES.FUND}
                onSelect={this.selectAlternativeFund}
              />
            ) : (
              <>
                <div className="fund-description">
                  <span className="fund-objective">{formatLocalizedString(fund.objective)}</span>
                </div>

                <div className="fund-stats">
                  {this.getFieldGroups(riskPeriod).map(g => (
                    <div key={g.i18nKey} className="stats-group">
                      <div className="group-header d-flex">
                        <h5 className="flex-fill">
                          <FormattedMessage id={g.i18nKey} />
                        </h5>

                        {g.controlled && (
                          <DateQuickRangeOneWay
                            selected={riskPeriod as TIME_PERIODS}
                            onChange={this.onRiskChange}
                            values={getDateQuickRangeFromPreset(TIME_RANGE_PRESET.SINGLE_ITEM_DEFAULT)}
                          />
                        )}
                      </div>

                      <div>
                        {g.fields.map(f => (
                          <div key={f.key} className="stat">
                            <div>
                              <FormattedMessage id={f.i18nKey} />
                              {this.generateField(fund, f)}
                            </div>
                          </div>
                        ))}

                        {g.getDisclaimer && <div className="stats-disclaimer">{g.getDisclaimer(fund)}</div>}
                      </div>
                    </div>
                  ))}
                </div>

                {fund.esg && (
                  <ConnectedPrivateComponent permissions={[PERMISSIONS.ESG]}>
                    {() => <FundDetailsESGMetrics fund={fund} appRegion={appRegion} />}
                  </ConnectedPrivateComponent>
                )}

                <div className="growth-graph">
                  <GrowthChart
                    user={user}
                    height={250}
                    items={[fundToComparisonItem(fund, appRegion)]}
                    localCurrency={user.appConfig.localCurrency}
                    locale={locale}
                    dateQuickRangeValues={getDateQuickRangeFromPreset(TIME_RANGE_PRESET.SINGLE_ITEM_DEFAULT)}
                  />
                </div>
              </>
            )}
          </PerfectScrollbar>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  complianceAlternatives: state.complianceAlternatives as Fund[],
  locale: state.intl.locale,
  appRegion: state.auth.user?.appConfig.appRegion as AppRegion,
  advancedSearchFunds: state.advancedSearch.fund.funds,
  selectedFunds: state.advancedSearch.fund.selectedFunds,
  comparingAlternatives: selectIsLoadingEvery(state, [ADD_COMPLIANCE_ALTERNATIVES.type]),
});

const mapDispatchToProps = {
  addFundComplianceAlternatives,
  addFundsToComparison,
  clearComplianceAlternatives,
  setSelectedFunds,
  updateFundsResult,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FundDetails));
